import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2 } from "lucide-react";
import api from "@/lib/api";

// Proper 7-finger cannabis fan leaf — hand-tuned path so it reads as
// unmistakably marijuana at small sizes. Filled, no stroke.
const CannabisLeaf = ({ className = "" }) => (
  <svg
    viewBox="0 0 64 64"
    className={className}
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g fill="currentColor">
      {/* center finger (tallest) */}
      <path d="M32 4 C33.4 14 33.4 26 32.6 36 L31.4 36 C30.6 26 30.6 14 32 4 Z" />
      {/* upper-right finger */}
      <path d="M32.6 32 C40 28 47 22 56 14 C54 24 50 31 34 36 Z" />
      {/* upper-left finger */}
      <path d="M31.4 32 C24 28 17 22 8 14 C10 24 14 31 30 36 Z" />
      {/* mid-right finger */}
      <path d="M34 34 C42 35 50 36 60 36 C56 41 50 43 35 39 Z" />
      {/* mid-left finger */}
      <path d="M30 34 C22 35 14 36 4 36 C8 41 14 43 29 39 Z" />
      {/* lower-right finger */}
      <path d="M34 38 C40 41 46 45 50 50 C42 49 36 45 33 41 Z" />
      {/* lower-left finger */}
      <path d="M30 38 C24 41 18 45 14 50 C22 49 28 45 31 41 Z" />
      {/* stem */}
      <rect x="31" y="36" width="2" height="22" rx="1" />
      {/* small calyx at base */}
      <ellipse cx="32" cy="36" rx="3" ry="2" />
    </g>
  </svg>
);

export const HeroSearch = ({ defaultValue = "", autoFocus = false, filters = null }) => {
  const [q, setQ] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState({ strains: [], brands: [] });
  const [loading, setLoading] = useState(false);
  const [matchCount, setMatchCount] = useState(null);
  const navigate = useNavigate();
  const boxRef = useRef();

  useEffect(() => {
    const handle = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) {
      setSuggestions({ strains: [], brands: [] });
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/autocomplete`, { params: { q } });
        setSuggestions(data);
        setOpen(true);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  // Live match-count preview for the Search button. Only fires when at
  // least one filter is active — reduces noise (and API load) on a bare
  // Home page. Debounced so slider drags don't hammer the endpoint.
  const hasActiveFilters =
    filters &&
    !!(
      filters.category ||
      filters.type ||
      filters.brand ||
      (filters.thc_min && filters.thc_min > 0) ||
      (filters.price_max && filters.price_max < 200) ||
      filters.in_stock_only
    );

  useEffect(() => {
    if (!hasActiveFilters) {
      setMatchCount(null);
      return;
    }
    const t = setTimeout(() => {
      const params = { count_only: 1 };
      if (filters.category) params.category = filters.category;
      if (filters.subcategory) params.subcategory = filters.subcategory;
      if (filters.size) params.size = filters.size;
      if (filters.type) params.type = filters.type;
      if (filters.brand) params.brand = filters.brand;
      if (filters.thc_min > 0) params.thc_min = filters.thc_min;
      if (filters.price_max && filters.price_max < 200)
        params.price_max = filters.price_max;
      if (filters.in_stock_only) params.in_stock_only = true;
      const term = q.trim();
      if (term) params.q = term;
      api
        .get("/search", { params })
        .then((r) => setMatchCount(r.data?.count ?? null))
        .catch(() => setMatchCount(null));
    }, 300);
    return () => clearTimeout(t);
  }, [
    hasActiveFilters,
    filters?.category,
    filters?.type,
    filters?.brand,
    filters?.thc_min,
    filters?.price_max,
    filters?.in_stock_only,
    q,
  ]);

  // Build the /search URL merging any Home-page filter selections (category,
  // strain type, brand, THC, price, in-stock) alongside the free-text query.
  // This is what lets Home behave like a proper "compose your search then hit
  // Search" experience: picking Vape + Modern Flower + THC 20%+ doesn't
  // navigate — those selections just ride along on the next submit.
  const buildUrl = (term) => {
    const params = new URLSearchParams();
    if (term) params.set("q", term);
    if (filters) {
      if (filters.category) params.set("category", filters.category);
      if (filters.subcategory) params.set("subcategory", filters.subcategory);
      if (filters.size) params.set("size", filters.size);
      if (filters.type) params.set("type", filters.type);
      if (filters.brand) params.set("brand", filters.brand);
      if (filters.thc_min > 0) params.set("thc_min", String(filters.thc_min));
      if (filters.price_max && filters.price_max < 200)
        params.set("price_max", String(filters.price_max));
      if (filters.in_stock_only) params.set("in_stock_only", "1");
    }
    return `/search?${params.toString()}`;
  };

  const submit = (e) => {
    e.preventDefault();
    const term = q.trim();
    // Allow submitting with just filters + no text query — the Search
    // button becomes a "browse filtered results" CTA on Home.
    const hasFilters =
      filters &&
      (filters.category ||
        filters.type ||
        filters.brand ||
        filters.thc_min > 0 ||
        (filters.price_max && filters.price_max < 200) ||
        filters.in_stock_only);
    if (term || hasFilters) navigate(buildUrl(term));
    setOpen(false);
  };

  const has = suggestions.strains.length || suggestions.brands.length;

  return (
    <div ref={boxRef} className="relative w-full max-w-2xl mx-auto">
      <form onSubmit={submit}>
        <div className="flex items-center bg-card/95 border border-border rounded-full pl-6 pr-2 h-16 shadow-[0_8px_40px_-12px_rgba(0,0,0,0.5)]">
          <Search className="w-5 h-5 text-muted-foreground mr-3" strokeWidth={1.6} />
          <div className="relative flex-1">
            <input
              data-testid="hero-search-input"
              autoFocus={autoFocus}
              value={q}
              onFocus={() => has && setOpen(true)}
              onChange={(e) => setQ(e.target.value)}
              className="w-full bg-transparent outline-none text-base"
            />
            {!q && (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 flex items-center gap-2 text-muted-foreground/80 tracking-wide"
              >
                <span className="font-serif">Bud</span>
                <CannabisLeaf className="w-6 h-6 text-primary" />
                <span className="font-serif">Search</span>
              </div>
            )}
          </div>
          <button
            type="submit"
            data-testid="hero-search-submit"
            className="h-12 px-5 sm:px-7 rounded-full bg-primary text-primary-foreground text-sm tracking-wide hover:bg-primary/90 inline-flex items-center gap-2 whitespace-nowrap"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <span>Search</span>
                {matchCount !== null && (
                  <span
                    data-testid="hero-search-count"
                    className="inline-flex items-center rounded-full bg-primary-foreground/15 px-2 py-0.5 text-[11px] tabular-nums leading-none"
                  >
                    {matchCount >= 1000
                      ? `${(matchCount / 1000).toFixed(1)}k`
                      : matchCount}
                  </span>
                )}
              </>
            )}
          </button>
        </div>
      </form>

      {open && has > 0 && (
        <div
          className="absolute left-0 right-0 mt-2 bg-card border border-border rounded-2xl p-3 z-[60] shadow-2xl shadow-black/50"
          data-testid="hero-search-suggestions"
        >
          {suggestions.strains.length > 0 && (
            <>
              <div className="eyebrow px-2 py-1">Strains</div>
              {suggestions.strains.map((s) => (
                <button
                  key={s.slug}
                  data-testid={`suggest-strain-${s.slug}`}
                  onClick={() => {
                    navigate(`/strains/${s.slug}`);
                    setOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-secondary flex items-center justify-between"
                >
                  <span className="font-serif">{s.name}</span>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    {s.type}
                  </span>
                </button>
              ))}
            </>
          )}
          {suggestions.brands.length > 0 && (
            <>
              <div className="eyebrow px-2 py-1 mt-2">Brands</div>
              {suggestions.brands.map((b) => (
                <button
                  key={b}
                  data-testid={`suggest-brand-${b}`}
                  onClick={() => {
                    navigate(`/search?q=${encodeURIComponent(b)}`);
                    setOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-secondary"
                >
                  {b}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};
